import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Skeleton,
  Tooltip,
  useMediaQuery
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  CheckCircleOutline as CheckCircleOutlineIcon
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import api from '../services/api';
import PageHeader from '../components/PageHeader';
import StatusBadge from '../components/StatusBadge';
import ConfirmDialog from '../components/ConfirmDialog';
import { useAuth } from '../contexts/AuthContext';
import ServerDataTable from '../components/ServerDataTable/ServerDataTable';
import TableSkeleton from '../components/ServerDataTable/TableSkeleton';
import { toast } from 'react-toastify';
import { formatDate } from '../utils/formatDate';

// Validation schema
const erpConfigSchema = Yup.object({
  erpName: Yup.string().required('ERP name is required'),
  apiBaseUrl: Yup.string().required('API Base URL is required'),
  client: Yup.string().required('Client is required'),
  company: Yup.string().required('Company is required'),
  username: Yup.string().required('Username is required'),
  password: Yup.string().when('id', {
    is: value => !value,
    then: () => Yup.string().required('Password is required'),
    otherwise: () => Yup.string()
  })
});

export default function ErpConfig() {
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [testingConnectionId, setTestingConnectionId] = useState(null);
  const [settingActiveId, setSettingActiveId] = useState(null);
  const [originalFormValues, setOriginalFormValues] = useState(null);
  const [hasFormChanges, setHasFormChanges] = useState(false);
  const [loadingRows, setLoadingRows] = useState([]);
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isAdmin = user?.role === 'admin';
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Columns
  const columns = [
    {
      field: 'erpName',
      headerName: 'ERP Name',
      width: 150,
      align: 'left',
      sortField: 'erp_name' // Map to database field name
    },
    {
      field: 'apiBaseUrl',
      headerName: 'API Base URL',
      width: 150,
      sortField: 'api_base_url' // Map to database field name
    },
    {
      field: 'client',
      headerName: 'Client',
      width: 100,
      sortField: 'client' // Map to database field name
    },
    {
      field: 'company',
      headerName: 'Company',
      width: 100,
      sortField: 'company' // Map to database field name
    },
    {
      field: 'isActive',
      headerName: 'Status',
      width: 150,
      sortField: 'is_active', // Map to database field name
      renderCell: ({ value }) => value ? <StatusBadge status='Active' color='success' /> : <StatusBadge status='Inactive' color='error' />
    },
    {
      field: 'createdAt',
      headerName: 'Created At',
      width: 200,
      sortField: 'created_at', // Map to database field name
      renderCell: ({ value }) => formatDate(value)
    },
    {
      field: 'updatedAt',
      headerName: 'Updated At',
      width: 200,
      sortField: 'updated_at', // Map to database field name
      renderCell: ({ value }) => formatDate(value)
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      align: 'right',
      renderCell: ({ row }) => {
        return (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton size="small" onClick={() => handleEdit(row)}>
              <EditIcon />
            </IconButton>
            <IconButton size="small" color="error" onClick={() => handleDelete(row)}>
              <DeleteIcon />
            </IconButton>
            {!row.isActive && (
              <Tooltip title='Set as Active'>
                <IconButton
                  size="small"
                  color="success"
                  onClick={() => handleSetActive(row)}
                  disabled={testingConnectionId !== null || settingActiveId !== null}
                  sx={{
                    bgcolor: 'rgba(76, 175, 80, 0.1)',
                    '&:hover': { bgcolor: 'rgba(76, 175, 80, 0.2)' }
                  }}
                >
                  {settingActiveId === row.id ? (
                    <CircularProgress size={20} color="success" />
                  ) : (
                    <CheckCircleOutlineIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      }
    }
  ];

  // Fetch active ERP configuration
  const { data: activeConfig, isLoading: isLoadingActive } = useQuery({
    queryKey: ['erpConfig', 'active'],
    queryFn: async () => {
      try {
        const response = await api.get('/erp-config/active');
        if (response.data?.status === 'success' && response.data?.data) {
          return response.data.data;
        }

        return null;
      } catch (error) {
        if (error.response?.status === 404) {
          return null; // No active config is not an error
        }
        throw error;
      }
    },
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true
  });

  // Fetch all ERP configurations (admin only)
  const { data: allConfigs, isLoading: isLoadingAll } = useQuery({
    queryKey: ['erpConfig', 'all', pageIndex, pageSize],
    queryFn: async () => {
      if (!isAdmin) return { data: [], pagination: { total: 0, totalPages: 0 } };
      try {
        const params = new URLSearchParams({
          page: pageIndex + 1,
          pageSize: pageSize.toString()
        });

        const response = await api.get(`/erp-config?${params.toString()}`);

        // Handle the standard API response format: { status: "success", data: configs, pagination: {...} }
        if (response.data && response.data.status === 'success') {
          return {
            data: response.data.data || [],
            pagination: response.data.pagination || { total: 0, totalPages: 0 }
          };
        }

        // Fallback for direct data response
        else if (Array.isArray(response.data)) {
          return {
            data: response.data,
            pagination: { total: response.data.length, totalPages: 1 }
          };
        }

        return { data: [], pagination: { total: 0, totalPages: 0 } };
      } catch (error) {
        console.error('Error fetching ERP configs:', error);
        return { data: [], pagination: { total: 0, totalPages: 0 } };
      }
    },
    enabled: isAdmin,
    staleTime: 1000 * 60 * 2, // 2 minutes
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true
  });

  // Create configuration mutation
  const createMutation = useMutation({
    mutationFn: async (configData) => {
      // Set new configurations as inactive by default
      const createData = { ...configData, isActive: false };
      const response = await api.post('/erp-config', createData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['erpConfig'] });
      queryClient.invalidateQueries({ queryKey: ['erpConfig', 'active'] });
      queryClient.invalidateQueries({ queryKey: ['configurationStatus', 'erp', 'active'] });
      setIsDialogOpen(false);
      setHasFormChanges(false);
      toast.success('ERP configuration created successfully');
    },
    onError: (error) => {
      const errorMessage = error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Failed to create ERP configuration';
      toast.error(errorMessage);
    }
  });

  // Update configuration mutation
  const updateMutation = useMutation({
    mutationFn: async (configData) => {
      const response = await api.put(`/erp-config/${configData.id}`, configData);
      return response.data;
    },
    onMutate: async (configData) => {
      // Add config ID to loading rows
      setLoadingRows(prev => [...prev, configData.id]);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['erpConfig'] });
      queryClient.invalidateQueries({ queryKey: ['erpConfig', 'active'] });
      queryClient.invalidateQueries({ queryKey: ['configurationStatus', 'erp', 'active'] });
      setIsDialogOpen(false);
      setSettingActiveId(null);
      setHasFormChanges(false);
      // Remove config ID from loading rows
      setLoadingRows(prev => prev.filter(id => id !== data.data.id));
      toast.success('ERP configuration updated successfully');
    },
    onError: (error, configData) => {
      const errorMessage = error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Failed to update ERP configuration';
      setSettingActiveId(null);
      // Remove config ID from loading rows
      setLoadingRows(prev => prev.filter(id => id !== configData.id));
      toast.error(errorMessage);
    }
  });

  // Delete configuration mutation
  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      const response = await api.delete(`/erp-config/${id}`);
      return response.data;
    },
    onMutate: async (id) => {
      // Add config ID to loading rows
      setLoadingRows(prev => [...prev, id]);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['erpConfig'] });
      queryClient.invalidateQueries({ queryKey: ['erpConfig', 'active'] });
      queryClient.invalidateQueries({ queryKey: ['configurationStatus', 'erp', 'active'] });
      setIsDeleteDialogOpen(false);
      // Remove all loading rows since data will refetch
      setLoadingRows([]);
      toast.success('ERP configuration deleted successfully');
    },
    onError: (error, id) => {
      const errorMessage = error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Failed to delete ERP configuration';
      // Remove config ID from loading rows
      setLoadingRows(prev => prev.filter(loadingId => loadingId !== id));
      toast.error(errorMessage);
    }
  });

  // Test connection mutation
  const testConnectionMutation = useMutation({
    mutationFn: async (configId) => {
      const response = await api.post(`/erp-config/${configId}/test-connection`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['erpConfig'] });
      queryClient.invalidateQueries({ queryKey: ['erpConfig', 'active'] });
      queryClient.invalidateQueries({ queryKey: ['configurationStatus', 'erp', 'active'] });
      toast.success('Connection test successful');
    },
    onError: (error) => {
      toast.error(`Connection test failed: ${error.response?.data?.message || error.message}`);
    },
    onSettled: () => {
      setTestingConnectionId(null);
    }
  });

  // Handle test connection
  const handleTestConnection = async (configId) => {
    setTestingConnectionId(configId);
    testConnectionMutation.mutate(configId);
  };

  // Handle set active
  const handleSetActive = async (config) => {
    setSettingActiveId(config.id);
    // Add config ID to loading rows
    setLoadingRows(prev => [...prev, config.id]);

    // Show testing message
    toast.info('Testing connection before activating configuration...');

    try {
      // First test the connection
      const response = await api.post(`/erp-config/${config.id}/test-connection`);

      if (response.data && response.data.status === 'success') {
        // Connection successful, proceed with activation
        toast.success('Connection test successful! Activating configuration...');

        const updatedConfig = { ...config, isActive: true };
        updateMutation.mutate(updatedConfig);
      } else {
        // Connection failed
        toast.error('Connection test failed. Cannot activate configuration.');
        setSettingActiveId(null);
        // Remove config ID from loading rows
        setLoadingRows(prev => prev.filter(id => id !== config.id));
      }
    } catch (error) {
      // Handle connection test error
      const errorMessage = error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Connection test failed';

      // Show detailed error message
      if (errorMessage.includes('Invalid credentials') || errorMessage.includes('unauthorized')) {
        toast.error('Connection failed: Invalid credentials. Please update your ERP credentials.');
      } else if (errorMessage.includes('not found') || errorMessage.includes('No configuration')) {
        toast.error('Connection failed: Configuration not found. Please check your configuration details.');
      } else if (errorMessage.includes('network') || errorMessage.includes('timeout')) {
        toast.error('Connection failed: Network error. Please check your internet connection.');
      } else if (errorMessage.includes('Invalid API URL') || errorMessage.includes('Invalid client')) {
        toast.error('Connection failed: Invalid API configuration. Please check your API settings.');
      } else {
        toast.error(`Connection failed: ${errorMessage}`);
      }

      console.error('ERP connection test failed:', error);
      setSettingActiveId(null);
      // Remove config ID from loading rows
      setLoadingRows(prev => prev.filter(id => id !== config.id));
    }
  };

  // Form handling
  const formik = useFormik({
    initialValues: {
      id: '',
      erpName: '',
      apiBaseUrl: '',
      client: '',
      company: '',
      username: '',
      password: '',
      hasGrantToken: false
    },
    validationSchema: erpConfigSchema,
    onSubmit: (values) => {
      const submitData = { ...values };

      // Remove non-API fields
      delete submitData.hasGrantToken;

      // Remove empty strings for optional fields
      Object.keys(submitData).forEach(key => {
        if (submitData[key] === '') {
          delete submitData[key];
        }
      });

      // Handle the submission
      if (submitData.id) {
        submitData.isActive = false;
        updateMutation.mutate(submitData);
      } else {
        createMutation.mutate(submitData);
      }
    }
  });

  // Track form changes
  useEffect(() => {
    if (originalFormValues) {
      const currentStringified = JSON.stringify(formik.values);
      const originalStringified = JSON.stringify(originalFormValues);
      setHasFormChanges(currentStringified !== originalStringified);
    }
  }, [formik.values, originalFormValues]);

  // Handle edit button click
  const handleEdit = (config) => {
    setSelectedConfig(config);
    const formValues = {
      id: config.id,
      erpName: config.erpName || '',
      apiBaseUrl: config.apiBaseUrl || '',
      client: config.client || '',
      company: config.company || '',
      username: config.username || '',
      password: '', // Don't populate password field
      hasGrantToken: config.hasGrantToken || false
    };
    formik.setValues(formValues);
    setOriginalFormValues(formValues);
    setIsDialogOpen(true);
  };

  // Handle add button click
  const handleAdd = () => {
    setSelectedConfig(null);
    const formValues = {
      id: '',
      erpName: '',
      apiBaseUrl: '',
      client: '',
      company: '',
      username: '',
      password: '',
      hasGrantToken: false
    };
    formik.resetForm({
      values: formValues
    });
    setOriginalFormValues(formValues);
    setIsDialogOpen(true);
  };

  // Handle delete button click
  const handleDelete = (config) => {
    setSelectedConfig(config);
    setIsDeleteDialogOpen(true);
  };

  // Handle dialog close
  const handleDialogClose = () => {
    setIsDialogOpen(false);
    formik.resetForm();
    setSelectedConfig(null);
    setOriginalFormValues(null);
    setHasFormChanges(false);
  };

  const handlePageChange = (newPage) => {
    setPageIndex(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPageIndex(0);
  };

  // Reset form when dialog closes
  useEffect(() => {
    if (!isDialogOpen) {
      formik.resetForm();
      setSelectedConfig(null);
      setOriginalFormValues(null);
      setHasFormChanges(false);
    }
  }, [isDialogOpen]);

  return (
    <Box>
      <PageHeader
        title="ERP Configuration"
        subtitle="Manage your ERP system connection settings"
      />
      {/* Active Configuration Card */}
      <Card sx={{ mb: 4, boxShadow: 10 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Active Configuration
            </Typography>
          </Box>

          {isLoadingActive ? (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  ERP Name
                </Typography>
                <Skeleton variant="text" width="50%" height={29} />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  API Base URL
                </Typography>
                <Skeleton variant="text" width="50%" height={29} />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Client
                </Typography>
                <Skeleton variant="text" width="50%" height={29} />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Company
                </Typography>
                <Skeleton variant="text" width="50%" height={29} />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Username
                </Typography>
                <Skeleton variant="text" width="50%" height={29} />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Created At
                </Typography>
                <Skeleton variant="text" width="50%" height={29} />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Last Updated
                </Typography>
                <Skeleton variant="text" width="50%" height={29} />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Status
                </Typography>
                <Skeleton variant="rectangular" width={80} height={29} sx={{ borderRadius: 1 }} />
              </Grid>
              {isAdmin && (
                <Grid item xs={12}>
                  <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                    <Skeleton variant="rectangular" width={150} height={41} sx={{ borderRadius: 1 }} />
                    <Skeleton variant="rectangular" width={100} height={41} sx={{ borderRadius: 1 }} />
                  </Box>
                </Grid>
              )}
            </Grid>
          ) : activeConfig ? (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  ERP Name
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {activeConfig.erpName}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  API Base URL
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {activeConfig.apiBaseUrl}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Client
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {activeConfig.client || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Company
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {activeConfig.company || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Username
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {activeConfig.username || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Created At
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {formatDate(activeConfig.createdAt)}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Last Updated
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {formatDate(activeConfig.updatedAt)}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Status
                </Typography>
                <StatusBadge status={activeConfig.isActive ? 'active' : 'inactive'} />
              </Grid>
              {isAdmin && (
                <Grid item xs={12}>
                  <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                    <Button
                      variant="contained"
                      startIcon={<RefreshIcon />}
                      onClick={() => handleTestConnection(activeConfig.id)}
                      disabled={testingConnectionId !== null || settingActiveId !== null}
                      sx={{ width: { xs: '100%', sm: '100%', md: 'auto' } }}
                    >
                      {testingConnectionId === activeConfig.id ? 'Testing...' : 'Test Connection'}
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<EditIcon />}
                      onClick={() => handleEdit(activeConfig)}
                      disabled={testingConnectionId !== null || settingActiveId !== null}
                      sx={{ width: { xs: '100%', sm: '100%', md: 'auto' } }}
                    >
                      Edit
                    </Button>
                  </Box>
                </Grid>
              )}

            </Grid>
          ) : (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                No active configuration found.
              </Typography>
              {isAdmin && (
                allConfigs?.data && allConfigs?.data?.length > 0 ? (
                  <Typography variant="body1" color="text.secondary" component="div" sx={{ mt: 2 }}>
                    Please activate below configuration to start syncing data.
                  </Typography>
                ) : (
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={handleAdd}
                    disabled={testingConnectionId !== null || settingActiveId !== null}
                    sx={{ mt: 2 }}
                  >
                    Create Now
                  </Button>
                )
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* All Configurations (Admin Only) */}
      {isAdmin && (
        <>
          {isLoadingAll ? (
            <TableSkeleton
              columns={columns}
              rows={allConfigs?.data || []}
              loading={isLoadingAll}
            />
          ) : (
            <ServerDataTable
              data={allConfigs?.data || []}
              columns={columns}
              totalRows={allConfigs?.pagination?.total || 0}
              currentPage={pageIndex}
              rowsPerPage={pageSize}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handlePageSizeChange}
              rowsPerPageOptions={[10, 25, 50, 100]}
              loading={isLoadingAll}
              loadingRows={loadingRows}
              noDataMessage="No configurations found."
              totalPages={allConfigs?.pagination?.totalPages || 0}
            />
          )}
        </>
      )}

      {/* Add/Edit Dialog */}
      <Dialog
        open={isDialogOpen}
        onClose={handleDialogClose}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ py: 2.5, px: 3, fontSize: 18 }}>
          {selectedConfig ? 'Edit ERP Configuration' : 'Add ERP Configuration'}
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          <Grid container spacing={isMobile ? 1 : 2} sx={{ mt: 0 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                id="erpName"
                name="erpName"
                label="ERP Name"
                placeholder="e.g., SAP, NetSuite, etc."
                value={formik.values.erpName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.erpName && Boolean(formik.errors.erpName)}
                helperText={formik.touched.erpName && formik.errors.erpName}
                variant="outlined"
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                id="apiBaseUrl"
                name="apiBaseUrl"
                label="API Base URL"
                placeholder="https://api.example.com"
                value={formik.values.apiBaseUrl}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.apiBaseUrl && Boolean(formik.errors.apiBaseUrl)}
                helperText={formik.touched.apiBaseUrl && formik.errors.apiBaseUrl}
                variant="outlined"
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                id="client"
                name="client"
                label="Client"
                value={formik.values.client}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.client && Boolean(formik.errors.client)}
                helperText={formik.touched.client && formik.errors.client}
                variant="outlined"
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                id="company"
                name="company"
                label="Company"
                value={formik.values.company}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.company && Boolean(formik.errors.company)}
                helperText={formik.touched.company && formik.errors.company}
                variant="outlined"
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                id="username"
                name="username"
                label="Username"
                value={formik.values.username}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.username && Boolean(formik.errors.username)}
                helperText={formik.touched.username && formik.errors.username}
                variant="outlined"
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                id="password"
                name="password"
                label="Password"
                type="password"
                value={formik.values.password}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.password && Boolean(formik.errors.password)}
                helperText={
                  (formik.touched.password && formik.errors.password) ||
                  (selectedConfig && 'Leave blank to keep current value')
                }
                variant="outlined"
                sx={{ mb: 2 }}
                autoComplete="new-password"
                inputProps={{
                  autoComplete: 'new-password' // inputProps autocomplete
                }}
              />
            </Grid>

          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleDialogClose} variant="contained" color="secondary" disabled={createMutation.isPending || updateMutation.isPending}>Cancel</Button>
          <Button
            onClick={formik.handleSubmit}
            type="submit"
            variant="contained"
            disabled={formik.isSubmitting || !formik.isValid || !hasFormChanges}
          >
            {selectedConfig ? (
              updateMutation.isPending ? 'Updating...' : 'Update'
            ) : (
              createMutation.isPending ? 'Creating...' : 'Create'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={isDeleteDialogOpen}
        title="Delete ERP Configuration"
        message={`Are you sure you want to delete the configuration for ${selectedConfig?.erpName}? This action cannot be undone.`}
        confirmText="Delete"
        confirmColor="error"
        onConfirm={() => deleteMutation.mutate(selectedConfig.id)}
        onCancel={() => setIsDeleteDialogOpen(false)}
        isLoading={deleteMutation.isPending}
      />
    </Box>
  );
}
